import React, { useState, useEffect } from 'react';
import AddAppointment from './AddAppointment';
import './Appointment.css';

const API_BASE = 'https://parachadental.com/dental-api';

const Appointment = () => {
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingAppointment, setEditingAppointment] = useState(null);
    const [filterStatus, setFilterStatus] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [totalPages, setTotalPages] = useState(1);

    // Fetch appointments
    const fetchAppointments = async () => {
        try {
            const response = await fetch(`${API_BASE}/fetch-appointments.php`);
            const data = await response.json();
            
            if (data.success) {
                setAppointments(data.appointments);
            }
        } catch (error) {
            console.error('Error fetching appointments:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAppointments();
    }, []);

    // Filter appointments - NOW INCLUDES patient_id search
    const filteredAppointments = appointments.filter(app => {
        const matchesStatus = filterStatus === 'all' || app.status === filterStatus;
        const matchesSearch = searchTerm === '' || 
            (app.patient_id && app.patient_id.toString().includes(searchTerm)) ||
            (app.patient_name && app.patient_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (app.patient_email && app.patient_email.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (app.patient_phone && app.patient_phone.includes(searchTerm));
        return matchesStatus && matchesSearch;
    });

    // Update total pages when filtered data changes
    useEffect(() => {
        setTotalPages(Math.ceil(filteredAppointments.length / itemsPerPage));
        setCurrentPage(1);
    }, [filteredAppointments.length, itemsPerPage]);

    // Get current page items
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredAppointments.slice(indexOfFirstItem, indexOfLastItem);

    // Change page
    const goToPage = (pageNumber) => {
        if (pageNumber >= 1 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
            document.querySelector('.appointments-table-wrapper')?.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const goToPreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const goToNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    // Generate page numbers to display
    const getPageNumbers = () => {
        const pageNumbers = [];
        const maxPagesToShow = 5;
        
        if (totalPages <= maxPagesToShow) {
            for (let i = 1; i <= totalPages; i++) {
                pageNumbers.push(i);
            }
        } else {
            const startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
            const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
            
            if (startPage > 1) {
                pageNumbers.push(1);
                if (startPage > 2) pageNumbers.push('...');
            }
            
            for (let i = startPage; i <= endPage; i++) {
                pageNumbers.push(i);
            }
            
            if (endPage < totalPages) {
                if (endPage < totalPages - 1) pageNumbers.push('...');
                pageNumbers.push(totalPages);
            }
        }
        return pageNumbers;
    };

    const handleAddAppointment = () => {
        fetchAppointments();
    };

    const handleEdit = (appointment) => {
        setEditingAppointment(appointment);
        setIsModalOpen(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this appointment?')) {
            try {
                const response = await fetch(`${API_BASE}/delete-appointment.php`, {
                    method: 'DELETE',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ id })
                });
                const data = await response.json();
                
                if (data.success) {
                    fetchAppointments();
                }
            } catch (error) {
                console.error('Error:', error);
            }
        }
    };

    const handleStatusChange = async (id, newStatus) => {
        const appointment = appointments.find(a => a.id === id);
        if (!appointment) return;

        try {
            const response = await fetch(`${API_BASE}/update-appointment.php`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...appointment, status: newStatus })
            });
            const data = await response.json();
            
            if (data.success) {
                fetchAppointments();
            }
        } catch (error) {
            console.error('Error:', error);
        }
    };

    // Function to calculate age from birth date
    const calculateAge = (birthDate) => {
        if (!birthDate) return 'N/A';
        const today = new Date();
        const birth = new Date(birthDate);
        let age = today.getFullYear() - birth.getFullYear();
        const monthDiff = today.getMonth() - birth.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
            age--;
        }
        return age;
    };

    const getStatusBadge = (status) => {
        switch(status) {
            case 'confirmed': return <span className="status-badge confirmed">Confirmed</span>;
            case 'pending': return <span className="status-badge pending">Pending</span>;
            case 'cancelled': return <span className="status-badge cancelled">Cancelled</span>;
            case 'completed': return <span className="status-badge completed">Completed</span>;
            default: return <span className="status-badge pending">{status}</span>;
        }
    };

    if (loading) return <div className="loading">Loading appointments...</div>;

    return (
        <div className="appointment-container-full">
            <div className="appointment-wrapper">
                {/* Header */}
                <div className="appointment-header">
                    <div>
                        <h1>Appointments</h1>
                        <p>Manage all patient appointments</p>
                    </div>
                    <button className="btn-create" onClick={() => { setEditingAppointment(null); setIsModalOpen(true); }}>
                        + Create New Appointment
                    </button>
                </div>

                {/* Filters */}
                <div className="filters-bar">
                    <div className="search-box">
                        <input 
                            type="text" 
                            placeholder="Search by Patient ID, Name, Email or Phone..." 
                            value={searchTerm} 
                            onChange={(e) => setSearchTerm(e.target.value)} 
                        />
                        {searchTerm && (
                            <div className="search-info">
                                🔍 Showing results for: <strong>{searchTerm}</strong>
                                <button className="clear-search" onClick={() => setSearchTerm('')}>✖ Clear</button>
                            </div>
                        )}
                    </div>
                    <div className="status-filters">
                        <button className={`filter-btn ${filterStatus === 'all' ? 'active' : ''}`} onClick={() => setFilterStatus('all')}>All</button>
                        <button className={`filter-btn ${filterStatus === 'pending' ? 'active' : ''}`} onClick={() => setFilterStatus('pending')}>Pending</button>
                        <button className={`filter-btn ${filterStatus === 'confirmed' ? 'active' : ''}`} onClick={() => setFilterStatus('confirmed')}>Confirmed</button>
                        <button className={`filter-btn ${filterStatus === 'completed' ? 'active' : ''}`} onClick={() => setFilterStatus('completed')}>Completed</button>
                        <button className={`filter-btn ${filterStatus === 'cancelled' ? 'active' : ''}`} onClick={() => setFilterStatus('cancelled')}>Cancelled</button>
                    </div>
                    <div className="items-per-page">
                        <label>Show:</label>
                        <select value={itemsPerPage} onChange={(e) => setItemsPerPage(Number(e.target.value))}>
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                        </select>
                    </div>
                </div>

                {/* Appointments Table */}
                <div className="appointments-table-wrapper">
                    {currentItems.length === 0 ? (
                        <div className="no-data">
                            <p>No appointments found</p>
                            <button className="btn-create" onClick={() => { setEditingAppointment(null); setIsModalOpen(true); }}>
                                Create your first appointment
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="table-responsive">
                                <table className="appointments-table">
                                    <thead>
                                        <tr>
                                            <th>Patient ID</th>
                                            <th>Patient Info</th>
                                            <th>Contact Details</th>
                                            <th>Appointment</th>
                                            <th>Doctor</th>
                                            <th>Status</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentItems.map((appointment) => (
                                            <tr key={appointment.id}>
                                                <td className="patient-id-cell">
                                                    <span className="patient-id-badge">#{appointment.patient_id}</span>
                                                </td>
                                                <td className="patient-info-cell">
                                                    <div className="patient-name">{appointment.patient_name}</div>
                                                    <div className="patient-meta">
                                                        {appointment.patient_gender && <span className="patient-gender">{appointment.patient_gender}</span>}
                                                        {appointment.patient_birth_date && (
                                                            <span className="patient-age">Age: {calculateAge(appointment.patient_birth_date)} yrs</span>
                                                        )}
                                                    </div>
                                                    {appointment.patient_address && (
                                                        <div className="patient-address">{appointment.patient_address}</div>
                                                    )}
                                                </td>
                                                <td className="contact-cell">
                                                    <div className="patient-email">{appointment.patient_email}</div>
                                                    <div className="patient-phone">{appointment.patient_phone}</div>
                                                </td>
                                                <td className="appointment-cell">
                                                    <div className="appointment-date">📅 {appointment.appointment_date}</div>
                                                    <div className="appointment-time">⏰ {appointment.appointment_time}</div>
                                                </td>
                                                <td className="doctor-cell">{appointment.doctor}</td>
                                                <td className="status-cell">{getStatusBadge(appointment.status)}</td>
                                                <td className="actions-cell">
                                                    <div className="action-buttons">
                                                        <select 
                                                            value={appointment.status} 
                                                            onChange={(e) => handleStatusChange(appointment.id, e.target.value)} 
                                                            className="status-select"
                                                        >
                                                            <option value="pending">Pending</option>
                                                            <option value="confirmed">Confirmed</option>
                                                            <option value="completed">Completed</option>
                                                            <option value="cancelled">Cancelled</option>
                                                        </select>
                                                        <button className="action-btn edit" onClick={() => handleEdit(appointment)}>Edit</button>
                                                        <button className="action-btn delete" onClick={() => handleDelete(appointment.id)}>Delete</button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="pagination-container">
                                    <div className="pagination-info">
                                        Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredAppointments.length)} of {filteredAppointments.length} appointments
                                    </div>
                                    <div className="pagination-controls">
                                        <button 
                                            onClick={goToPreviousPage} 
                                            disabled={currentPage === 1}
                                            className="pagination-btn"
                                        >
                                            &laquo; Previous
                                        </button>
                                        
                                        <div className="pagination-numbers">
                                            {getPageNumbers().map((page, index) => (
                                                page === '...' ? (
                                                    <span key={index} className="pagination-dots">...</span>
                                                ) : (
                                                    <button
                                                        key={index}
                                                        onClick={() => goToPage(page)}
                                                        className={`pagination-number ${currentPage === page ? 'active' : ''}`}
                                                    >
                                                        {page}
                                                    </button>
                                                )
                                            ))}
                                        </div>
                                        
                                        <button 
                                            onClick={goToNextPage} 
                                            disabled={currentPage === totalPages}
                                            className="pagination-btn"
                                        >
                                            Next &raquo;
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Statistics */}
                <div className="stats-summary">
                    <div className="stat-item">
                        <span className="stat-label">Total:</span>
                        <span className="stat-value">{filteredAppointments.length}</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-label">Pending:</span>
                        <span className="stat-value pending">{appointments.filter(a => a.status === 'pending').length}</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-label">Confirmed:</span>
                        <span className="stat-value confirmed">{appointments.filter(a => a.status === 'confirmed').length}</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-label">Completed:</span>
                        <span className="stat-value completed">{appointments.filter(a => a.status === 'completed').length}</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-label">Cancelled:</span>
                        <span className="stat-value cancelled">{appointments.filter(a => a.status === 'cancelled').length}</span>
                    </div>
                </div>
            </div>

            <AddAppointment isOpen={isModalOpen} onClose={() => { setIsModalOpen(false); setEditingAppointment(null); }} onSave={handleAddAppointment} appointmentToEdit={editingAppointment} />
        </div>
    );
};

export default Appointment;